import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { Combobox } from '../../components/ui/Combobox';
import { ApiError } from '../../lib/api';
import { formatRelativeTime } from '../../lib/relativeTime';
import { useClickOutside } from '../../lib/useClickOutside';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import type { MetaAdDraftSummary } from '../../types/metaAdDraft';
import { archiveDraft, createDraft, duplicateDraft, listDrafts } from './api';
import { emptyMetaAdDraftInput } from './emptyDraft';
import { DuplicateModal } from './components/DuplicateModal';
import { useDebouncedValue } from './useDebouncedValue';
import {
  DEFAULT_DRAFT_SORT,
  DRAFT_STATUS_FILTER_OPTIONS,
  displayStatus,
  hasFailedPublishIndicator,
  nextDraftSort,
  viewDrafts,
  type DraftDisplayStatus,
  type DraftSort,
  type DraftSortKey,
  type DraftStatusFilter,
} from './draftListView';

// Stage 7A E1 — Meta Ads drafts list. Fetch-all + client filter/sort/search +
// Load-more (surveys precedent). "Archived" = soft-deleted (deletedAt); the badge
// + filter derive from it. Per-row Archive (owner/admin), failed-publish dot.

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<DraftDisplayStatus, string> = {
  draft: 'Draft',
  publishing: 'Publishing…',
  published: 'Published',
  failed: 'Draft',
  cancelled: 'Cancelled',
  archived: 'Archived',
};
const STATUS_VARIANT: Record<DraftDisplayStatus, BadgeVariant> = {
  draft: 'neutral',
  publishing: 'info',
  published: 'success',
  failed: 'neutral',
  cancelled: 'neutral',
  archived: 'neutral',
};

export function MetaAdsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useCurrentUser();

  const drafts = useQuery({ queryKey: ['metaAdDrafts'], queryFn: listDrafts });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [status, setStatus] = useState<DraftStatusFilter>('all');
  const [accountId, setAccountId] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [sort, setSort] = useState<DraftSort>(DEFAULT_DRAFT_SORT);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [pendingArchive, setPendingArchive] = useState<MetaAdDraftSummary | null>(null);
  const [pendingDuplicate, setPendingDuplicate] = useState<MetaAdDraftSummary | null>(null);

  const accountName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? '—';

  // Account filter options — only accounts that actually appear in the drafts (no
  // per-AM access model yet; list is staff-wide).
  const accountOptions = useMemo(() => {
    const ids = Array.from(new Set((drafts.data?.drafts ?? []).map((d) => d.accountId)));
    const opts = ids
      .map((id) => ({ value: id, label: accountName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: '', label: 'All accounts' }, ...opts];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts.data, accounts.data]);

  const filtered = useMemo(() => {
    const all = drafts.data?.drafts ?? [];
    const byAccount = accountId ? all.filter((d) => d.accountId === accountId) : all;
    return viewDrafts(byAccount, { status, search, sort });
  }, [drafts.data, accountId, status, search, sort]);

  // Reset the load-more window whenever the view changes.
  useEffect(() => setVisible(PAGE_SIZE), [status, accountId, search, sort]);

  const onSort = (key: DraftSortKey) => setSort((cur) => nextDraftSort(cur, key));

  const create = useMutation({
    mutationFn: () => createDraft(emptyMetaAdDraftInput()),
    onSuccess: ({ draft }) => navigate(`/advertising/meta/compose/${draft.id}`),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not create a draft.'),
  });

  const archive = useMutation({
    mutationFn: (id: string) => archiveDraft(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
      setPendingArchive(null);
      toast.success('Draft archived.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not archive the draft.'),
  });

  const duplicate = useMutation({
    mutationFn: ({ id, newTitle }: { id: string; newTitle: string }) => duplicateDraft(id, newTitle),
    onSuccess: ({ draft }) => {
      qc.invalidateQueries({ queryKey: ['metaAdDrafts'] });
      setPendingDuplicate(null);
      navigate(`/advertising/meta/compose/${draft.id}`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not duplicate the draft.'),
  });

  const canArchive = (d: MetaAdDraftSummary) => me.role === 'admin' || d.createdBy.uid === me.uid;
  const isNavigable = (d: MetaAdDraftSummary) => d.deletedAt === null; // archived can't be opened (no un-archive in MVP)
  const open = (d: MetaAdDraftSummary) => {
    if (isNavigable(d)) navigate(`/advertising/meta/compose/${d.id}`);
  };

  const shown = filtered.slice(0, visible);

  return (
    <div>
      <PageHeader
        title="Meta Ads"
        description="Build and publish Meta campaigns."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/advertising/meta/add-to-live')}>
              Add an ad to a live ad set
            </Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? 'Creating…' : '+ New Draft'}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SelectField
          value={status}
          onValueChange={(v) => setStatus(v as DraftStatusFilter)}
          size="sm"
          className="w-36"
          options={DRAFT_STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        {accountOptions.length > 2 && (
          <Combobox
            aria-label="Filter by account"
            searchPlaceholder="Search accounts…"
            value={accountId}
            onValueChange={setAccountId}
            size="sm"
            className="w-52"
            options={accountOptions}
          />
        )}
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by title…"
          className="w-64 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
      </div>

      {drafts.isLoading && <CenteredSpinner />}
      {drafts.error && <p className="text-sm text-red-600">Couldn't load drafts.</p>}

      {drafts.data && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
          <p className="text-sm font-medium text-slate-700">
            {drafts.data.drafts.length === 0 ? 'No ad drafts yet' : 'No drafts match your filters'}
          </p>
          {drafts.data.drafts.length === 0 && (
            <div className="mt-4">
              <Button onClick={() => create.mutate()} disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create your first ad draft'}
              </Button>
            </div>
          )}
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="overflow-visible rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortHeader label="Title" sortKey="title" sort={sort} onSort={onSort} />
                  <th className="px-4 py-2 font-medium">Account</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <SortHeader label="Updated" sortKey="updated" sort={sort} onSort={onSort} />
                  <SortHeader label="Created" sortKey="created" sort={sort} onSort={onSort} />
                  <th className="px-4 py-2 font-medium">Last edited by</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {shown.map((d) => {
                  const ds = displayStatus(d);
                  const navigable = isNavigable(d);
                  return (
                    <tr
                      key={d.id}
                      onClick={() => open(d)}
                      onKeyDown={(e) => {
                        if (navigable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          open(d);
                        }
                      }}
                      role={navigable ? 'button' : undefined}
                      tabIndex={navigable ? 0 : undefined}
                      className={
                        navigable
                          ? 'cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none'
                          : 'text-slate-400'
                      }
                    >
                      <td className="px-4 py-3 font-medium text-slate-800">
                        {d.title || 'Untitled'}
                        {d.attachedAdSetName && (
                          <span className="mt-0.5 block text-xs font-normal text-slate-500">Added to {d.attachedAdSetName}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{accountName(d.accountId)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          <Badge variant={STATUS_VARIANT[ds]}>{STATUS_LABEL[ds]}</Badge>
                          {hasFailedPublishIndicator(d) && (
                            <span
                              className="inline-block h-2 w-2 rounded-full bg-red-500"
                              title={`Last publish failed: ${d.lastPublishError}`}
                              aria-label="Last publish failed"
                            />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600" title={new Date(d.updatedAt).toLocaleString()}>
                        {formatRelativeTime(d.updatedAt)}
                      </td>
                      <td className="px-4 py-3 text-slate-600" title={new Date(d.createdAt).toLocaleString()}>
                        {formatRelativeTime(d.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{(d.updatedBy ?? d.createdBy).name}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {navigable ? (
                          <RowMenu
                            onDuplicate={() => setPendingDuplicate(d)}
                            onArchive={canArchive(d) ? () => setPendingArchive(d) : undefined}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length > shown.length && (
            <div className="mt-4 text-center">
              <Button variant="outline" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Load more ({filtered.length - shown.length} more)
              </Button>
            </div>
          )}
        </>
      )}

      {pendingArchive && (
        <ConfirmArchive
          draft={pendingArchive}
          busy={archive.isPending}
          onCancel={() => setPendingArchive(null)}
          onConfirm={() => archive.mutate(pendingArchive.id)}
        />
      )}

      {pendingDuplicate && (
        <DuplicateModal
          sourceTitle={pendingDuplicate.title || 'Untitled'}
          busy={duplicate.isPending}
          onCancel={() => setPendingDuplicate(null)}
          onConfirm={(newTitle) => duplicate.mutate({ id: pendingDuplicate.id, newTitle })}
        />
      )}
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: DraftSortKey;
  sort: DraftSort;
  onSort: (key: DraftSortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="px-4 py-2 font-medium"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-700"
      >
        <span>{label}</span>
        <span aria-hidden className="text-[10px] text-slate-400">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function RowMenu({ onDuplicate, onArchive }: { onDuplicate: () => void; onArchive?: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);
  const close = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded p-1 text-slate-400 hover:bg-slate-100"
        aria-label="Draft actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-32 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={close(onDuplicate)}
            className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            Duplicate
          </button>
          {onArchive && (
            <button
              type="button"
              onClick={close(onArchive)}
              className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-slate-50"
            >
              Archive
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmArchive({
  draft,
  busy,
  onCancel,
  onConfirm,
}: {
  draft: MetaAdDraftSummary;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800">Archive this draft?</h3>
        <p className="mt-2 text-sm text-slate-600">
          “{draft.title || 'Untitled'}” will move to the Archived filter. You can recover it from there.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? 'Archiving…' : 'Archive'}
          </Button>
        </div>
      </div>
    </div>
  );
}

